<template>
  <!-- In diff mode groups are not clickable, so render a plain div: an <a>
       would still get the link colour and Bootstrap's darker :hover colour,
       which reads as a hover affordance. -->
  <component
    :is="diff ? 'div' : 'a'"
    ref="container"
    class="row text-decoration-none"
    :class="{ 'text-primary': !diff, 'cursor-pointer': !diff }"
  >
    <form
      v-if="editingTitle"
      class="col"
      @submit.prevent="changeTitle"
    >
      <b-input-group
        ref="titleInput"
        class="ms-1 mt-2 mb-2"
        :style="{height: '38px'}"
        :disabled="saving"
        @click.stop
      >
        <template #append>
          <b-button
            variant="outline-secondary"
            :disabled="saving"
            @click="editingTitle = false"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            type="submit"
            :data-test="`change-group-title-submit_${group.name}`"
            :disabled="saving"
          >
            <template v-if="saving">
              Saving...
              <b-spinner
                class="mb-1"
                small
              />
            </template>
            <template v-else>
              Save
            </template>
          </b-button>
        </template>
        <b-input
          v-model="title"
          :data-test="`change-group-title-text-input_${group.name}`"
          placeholder="Group Title"
          :disabled="saving"
        />
      </b-input-group>
    </form>
    <!-- In diff mode the statistics table carries three numeric columns
         (A, B, delta) with nine-digit counts, so it gets two thirds of the
         row; the single report keeps the even split. -->
    <div
      v-else
      class="mb-2"
      :class="diff ? 'col-4' : 'col'"
    >
      <h2 data-test="group-title">
        <!-- eslint-disable vue/no-v-html -- initialTitle: SearchBox-highlighted group title (regex matches wrapped in `<span class="bg-warning">` over server-trusted text) -->
        <span
          :data-test="`group-title_${group.name}`"
          v-html="initialTitle"
        />
        <!-- eslint-enable vue/no-v-html -->
        <i
          v-if="deployAll && !diff"
          class="bi bi-pencil ms-3"
          :data-test="`change-group-title-link_${group.name}`"
          @click.stop="editTitle"
        />
      </h2>
      <b-button
        v-if="deployAll && !diff"
        size="sm"
        variant="outline-secondary"
        class="ms-1"
        :data-test="`change-column-order-${group.name}`"
        @click.stop="$emit('changeColumnOrder')"
      >
        Change Order of Columns in this Group
      </b-button>
    </div>
    <FileColumnStatistics
      :total-rows="totalRows"
      :group="group"
      :diff="diff"
      :diff-metric="diffMetric"
      :total-rows-b="totalRowsB"
      :class="diff ? 'col-8 pe-4' : 'col me-3'"
      @column-clicked="columnClicked"
    />
  </component>
</template>

<script>
  import FileColumnStatistics from './FileColumnStatistics.vue'
  export default {
    components: {FileColumnStatistics},
    props: {
      initialTitle: {type: String, default: null},
      group: {type: Object, default: null},
      totalRows: {type: Number, default: null},
      diff: {type: Boolean, default: false},
      diffMetric: {type: String, default: "count"},
      totalRowsB: {type: Number, default: null},
    },
    emits: ['changeColumnOrder', 'columnClicked', 'titleChanged'],
    data() {
      return {editingTitle: false, title: this.initialTitle, saving: false};
    },
    computed: {
      deployAll: () => ['true', 't', 'yes', 'y', 'on'].includes(
        process.env.VUE_APP_DEPLOY_ALL.toLowerCase()
      ),
      backendUrl: () => process.env.VUE_APP_VISUALIZER_BACKEND_URL,
      backendToken: () => process.env.VUE_APP_BACKEND_TOKEN,
    },
    methods: {
      editTitle() {
        // Seed the edit box with the plain group title, NOT initialTitle -
        // initialTitle is the SearchBox-highlighted string (matches wrapped in
        // <span class="bg-warning">) and would show raw markup in the input.
        // Setting it on every open also avoids showing a stale title from a
        // previous search/edit.
        this.title = this.group.title;
        this.editingTitle = true;
        this.$nextTick(() => this.$refs.titleInput.children[0].focus());
      },
      async changeTitle() {
        this.saving = true;
        const taskId = this.$route.params.taskId;
        const urlBase = `change-group-title/${taskId}`;
        const url = `${this.backendUrl}/${urlBase}/?token=${this.backendToken}`;
        const groupName = (
          this.group.name.endsWith('__columngroup') ?
            this.group.name.substr(0, this.group.name.length - 13) :
            this.group.name
        );
        const data = {name: groupName, title: this.title};
        await fetch(url, {method: 'POST', body: JSON.stringify(data)});
        this.$emit('titleChanged', this.title);
        this.editingTitle = false;
        this.saving = false;
      },
      columnClicked(clickType, column, group) {
        this.$emit('columnClicked', clickType, column, group);
      }
    }
  };
</script>
